import {
    getDocs,
    query,
    where,
    Query,
    DocumentData,
    limit,
    orderBy
} from 'firebase/firestore';

import AnimalService, {
    AnimalClass,
    AnimalMother,
    AnimalConstants
} from '../animal';
import LandService, { LandClass } from '../land';
import { AnimalCounter } from '../animal/animalClass';
import ErrorInterface from '../interfaces/errorInterface';

interface searchReturnType {
    count: number;
    data: AnimalClass[];
}

export default class LandAnimalService {
    animalService = new AnimalService();
    landService = new LandService();
    lands: LandClass[];

    constructor(lands?: LandClass[]) {
        this.lands = lands || [];
    }

    /**
     * Get the list of references of animals based on their lands
     * @param land LandClass[] The list of lands
     * @returns Query<DocumentData>[] List of Animals Queries
     */
    private getAnimalsInLandsQueries = (
        lands: LandClass[]
    ): Query<DocumentData>[] => {
        const collectionRef = this.animalService.queryCollection;
        const allQuery = lands.reduce((acc, cur, idx, arr) => {
            if (arr[idx * 10]) {
                const ids = arr
                    .slice(idx * 10, idx * 10 + 10)
                    .map((land) => land.id);

                const queryRef = query(collectionRef, where('land', 'in', ids));
                acc.push(queryRef);
            }
            return acc;
        }, [] as Query<DocumentData>[]);

        return allQuery;
    };

    /**
     * Get the FILTERED list of references of animals based on their lands
     * @param filters the filters to use on the searching
     * @param land LandClass[] The list of lands
     * @returns Query<DocumentData>[] List of Animals Queries
     */
    private searchQueries = (
        filters: null | Record<string, any>,
        lands?: LandClass[]
    ): Query[] => {
        const allQueries = filters?.land?.value
            ? [this.animalService.queryCollection]
            : this.getAnimalsInLandsQueries(lands || this.lands);

        return allQueries.reduce((acc, queryRef) => {
            if (filters && Object.keys(filters).length) {
                Object.values(filters).forEach((filter) => {
                    if (filter.comparison && filter.value) {
                        queryRef = query(
                            queryRef,
                            where(filter.field, filter.comparison, filter.value)
                        );
                    }
                });
            }

            acc = !filters?.identifier
                ? [...acc, queryRef]
                : [
                      ...acc,
                      query(
                          queryRef,
                          where('externalId', '==', filters.identifier)
                      ),
                      query(
                          queryRef,
                          where('internalId', '==', filters.identifier)
                      )
                  ];
            return acc;
        }, [] as Query[]);
    };

    /**
     * Simple Animal search
     * @param filter the filters to use on the searching
     * @param lands The lands that owns the animals
     * @returns List of animals as AnimalClass
     */
    search = async (
        filters: null | Record<string, any>,
        lands?: LandClass[]
    ): Promise<AnimalClass[]> => {
        const allQueries = this.searchQueries(filters, lands);

        const allDocs = (
            await Promise.all(
                allQueries.map(async (eachQuery) => getDocs(eachQuery))
            )
        ).reduce((acc, docs) => [...acc, ...docs.docs], [] as any);

        const animals = allDocs.reduce(
            (acc: any, doc: DocumentData) => {
                if (!acc.ids.includes(doc.id)) {
                    acc.animal = new AnimalClass({
                        id: doc.id,
                        ...doc.data()
                    });
                    lands?.length && acc.animal.setLand(lands);
                    acc.ids.push(doc.id);
                    acc.values = [...acc.values, acc.animal];
                }
                return acc;
            },
            { ids: [] as string[], values: [] as AnimalClass[], animal: null }
        );

        return animals.values as AnimalClass[];
    };

    /**
     * Sorted Animal search
     * @param filter the filters to use on the searching
     * @param lands The lands that owns the animals
     * @param allResults Auxiliar parameter so return all the results of a summary of them
     * @returns List of animals as AnimalClass
     */
    sortedSearch = async (
        filters: null | Record<string, any>,
        lands: LandClass[],
        allResults?: boolean
    ): Promise<searchReturnType> => {
        const allQueries = this.searchQueries(filters, lands);

        const animalsInLands = lands.reduce((acc, land) => {
            const landCounter = land.animals.reduce((acc, counter) => {
                acc += counter.amount;
                return acc;
            }, 0);
            acc += landCounter;
            return acc;
        }, 0);

        let count = 0;
        const allDocs = (
            await Promise.all(
                allQueries.map(async (eachQuery) => {
                    const docs = await getDocs(eachQuery);
                    if (animalsInLands < 100) {
                        return docs;
                    } else {
                        count += docs.size;
                        return allResults
                            ? docs
                            : getDocs(
                                  query(
                                      eachQuery,
                                      limit(100),
                                      orderBy('birthDate', 'desc')
                                  )
                              );
                    }
                })
            )
        ).reduce((acc, docs) => [...acc, ...docs.docs], [] as any);

        const animals = allDocs.reduce(
            (acc: any, doc: DocumentData) => {
                if (!acc.ids.includes(doc.id)) {
                    acc.animal = new AnimalClass({
                        id: doc.id,
                        ...doc.data()
                    });
                    lands?.length && acc.animal.setLand(lands);
                    acc.ids.push(doc.id);
                    acc.values = [...acc.values, acc.animal];
                }
                return acc;
            },
            { ids: [] as string[], values: [] as AnimalClass[], animal: null }
        );

        animals.values.sort((a: any, b: any) => {
            if (a.getBirthday() && b.getBirthday()) {
                return b.getBirthday().getTime() - a.getBirthday().getTime();
            } else if (a.getBirthday()) {
                return -1;
            } else if (b.getBirthday()) {
                return 1;
            }
            return b.externalId.localeCompare(a.externalId);
        });

        return {
            data: animals.values as AnimalClass[],
            count
        } as searchReturnType;
    };

    /**
     * Get the list of all land's animals
     * @param land The land to get the animals for
     * @returns List of animals as AnimalClass
     */
    getAnimalsInLands = async (
        lands: LandClass[]
    ): Promise<AnimalClass[] | ErrorInterface> => {
        const animals = await this.search({}, lands);
        return animals as AnimalClass[];
    };

    /**
     * Get the amount of land's animals
     * @param land LandClass The land to get the animals for
     * @returns Counting of animals as AnimalCounter
     */
    countAnimalsInLand = async (land: LandClass): Promise<LandClass> => {
        const indexes: string[] = [];
        const counters: AnimalCounter[] = [];
        const animals = (await this.getAnimalsInLands([land])) as AnimalClass[];
        let amount: number;
        let index: number;
        animals.forEach((animal) => {
            amount = 0;

            if (
                animal.type === AnimalConstants.animalTypes.adult ||
                animal.type === AnimalConstants.animalTypes.child
            ) {
                amount = 1;
            } else if (animal.type === AnimalConstants.animalTypes.group) {
                amount = animal.malesAmount + animal.femalesAmount;
            }

            index = indexes.indexOf(
                `${animal.specie}-${animal.type}-${animal.gender}`
            );
            if (index === -1) {
                indexes.push(
                    `${animal.specie}-${animal.type}-${animal.gender}`
                );
                counters.push(new AnimalCounter(amount, { ...animal }));
            } else {
                counters[index].add(amount);
            }
        });
        land.animals = counters;
        const saved = await this.landService.save(land);
        return saved as LandClass;
    };

    /**
     * Get the animal with its children as a new class
     * @param animal The mother animal
     * @returns List of animals as AnimalClass
     */
    getAnimalWithChildrens = async (
        animal: AnimalClass,
        lands: LandClass[]
    ): Promise<AnimalMother> => {
        const filters = {
            mother: {
                field: 'mother.id',
                comparison: '==',
                value: animal.id
            }
        };
        const childs = await this.search(filters, lands);
        const mother = new AnimalMother(animal, childs);
        return mother as AnimalMother;
    };
}
